<template>
  <div class="row justify-content-xxl-between justify-content-center">
    <div
      v-for="card in filterCardByProductId"
      :key="`large-${card.label}`"
      class="col-xxl-auto col-lg-5 col-auto"
    >
      <v-card-large
        :modal-component="card.type"
        :is-editable="card.editable"
        :color="card.color"
        class="mb-3 mx-auto"
        @edit="openModal"
      >
        <template #title>
          {{ card.label }}
        </template>
        <template #content>
          {{ card.getter() }}
        </template>
      </v-card-large>
    </div>
  </div>
</template>
<script>
import { get } from 'lodash-es';
import { mapGetters } from 'vuex';

import { ui } from '@/mixins';
import { toDateFrNumeric } from '@/utils/_Date';

import ContractLargeCard from '@/components/contracts/cards/ContractLargeCard.vue';

const [RCP, RDD, CYBER, MRP, TELEWORK, TRI, MISSION] = [1, 2, 5, 16, 42, 23, 13];

export default {
  components: {
    'v-card-large': ContractLargeCard,
  },
  mixins: [ui],
  data() {
    return {
      cards: [
        // first cards
        {
          label: 'Convention collective',
          products: [18],
          getter: this.getConventionId,
          color: 'blue',
        },
        {
          label: 'Activité',
          products: [RCP, RDD, CYBER, TELEWORK, TRI],
          getter: this.getMainActivityName,
          editable: true,
          type: 'main-activity',
          color: 'blue',
        },
        {
          label: 'Activité',
          products: [MRP],
          getter: this.getMainActivityName,
          editable: true,
          type: 'local-activity',
          color: 'blue',
        },
        {
          label: 'Identité',
          products: [4],
          getter: this.getBeneficiaryIdentity,
          color: 'blue',
        },

        // second cards
        {
          label: 'Salariés assurés',
          products: [TELEWORK],
          editable: true,
          getter: this.getEmployeesBrackets,
          type: 'effectif-levels',
          color: 'yellow',
        },
        // effectif
        {
          label: 'Effectifs',
          products: [MISSION],
          getter: this.getEffectif,
          type: 'effectif',
          color: 'yellow',
          editable: true,
        },
        {
          label: 'Formule de garantie',
          products: [MRP, MISSION],
          getter: this.getLiabilityName,
          editable: true,
          type: 'formula',
          color: 'yellow',
        },
        {
          label: 'Montant de garantie',
          products: [RCP, RDD, CYBER, 9],
          getter: this.getLiabilityName,
          editable: true,
          type: 'liability',
          color: 'yellow',
        },
        {
          label: 'Moyenne d\'âge',
          products: [18],
          getter: this.getAverageAge,
          color: 'yellow',
        },
        {
          label: 'Date de naissance',
          products: [4],
          getter: this.getBirthDate,
          color: 'yellow',
        },
        {
          label: 'Térritorlité',
          products: [TRI],
          getter: this.getTerritorialite,
          editable: true,
          type: 'territorialite',
          color: 'yellow',
        },
        // third card
        {
          label: 'Date d\'effet',
          products: [RCP, RDD, 4, CYBER, 9, MRP, 18, TELEWORK, TRI, MISSION],
          getter: this.getEffectiveDate,
          color: 'pink',
        },

        // last card
        {
          label: 'Échéance',
          products: [RCP, RDD, 4, CYBER, 9, MRP, 18, TELEWORK, TRI, MISSION],
          getter: this.getEcheance,
          color: 'green',
        },
      ],
    };
  },
  computed: {
    ...mapGetters({
      contract: 'contract/get',
    }),
    canEditAmendmentSubscription() {
      return this.contract.status === 'AMENDMENT'
        && this.contract.leadStatus !== 'AMENDMENT_VALIDATED'
        && Boolean(this.contract.policyId)
        && !this.contract.activeVersionId
        && this.contract.delegation
        && !this.contract.derogation;
    },
    filterCardByProductId() {
      return this.cards
        .filter(({ products }) => products.includes(this.contract.productId))
        .map((card) => Object.assign({}, card, {
          editable: this.isCardEditable(card),
        }));
    },
  },
  methods: {
    toDateFrNumeric,
    isCardEditable(card) {
      if (!this.canEditAmendmentSubscription || !card.editable) {
        return false;
      }

      if (['formula', 'liability'].includes(card.type)) {
        return get(this, 'contract.liabilities.length', 0) > 1;
      }

      return true;
    },
    openModal(type) {
      this.ui_showModalAmendment(type, true);
    },
    formatPrice(price) {
      const formattedPrice = parseFloat(price || 0)
        .toFixed(2)
        .toString()
        .replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
        .replace('.', ',');
      return `${formattedPrice} €`;
    },

    getBirthDate() {
      const birthdate = get(this.contract, 'risk.beneficiary.birthDate');
      if (birthdate) {
        return birthdate.split('-').reverse().join('/');
      }
      return 'N/A';
    },
    getConventionId() {
      const id = get(this.contract, 'risk.convention.idcc');
      return id || 'N/A';
    },
    getAverageAge() {
      const executiveAverage = get(this.contract, 'risk.executivesAverageAge');
      const nonExecutiveAverage = get(this.contract, 'risk.nonExecutivesAverageAge');
      if (executiveAverage && nonExecutiveAverage) {
        return `${parseInt((executiveAverage + nonExecutiveAverage) / 2, 10)} ans`;
      }
      const average = executiveAverage || nonExecutiveAverage;
      if (average) { return `${average} ans` }
      return 'N/A';
    },
    getBeneficiaryIdentity() {
      const civility = get(this.contract, 'risk.beneficiary.civility');
      const firstname = get(this.contract, 'risk.beneficiary.firstName');
      const lastname = get(this.contract, 'risk.beneficiary.lastName');
      if (civility && firstname && lastname) {
        return `${civility}. ${lastname} ${firstname}`;
      }
      if (firstname && lastname) {
        return `${lastname} ${firstname}`;
      }
      return firstname || lastname || 'N/A';
    },
    getMainActivityName() {
      return get(this.contract, 'risk.mainActivity.name') || 'N/A';
    },
    getLocalActivityName() {
      return get(this.contract, 'risk.local.activity.name') || 'N/A';
    },
    getLiabilityName() {
      const { liabilities } = this.contract;
      if (liabilities && liabilities.length > 0) {
        const liability = liabilities.find(({ enabled }) => enabled);
        if (liability) { return liability.name }
      }
      return 'N/A';
    },
    getEmployeesBrackets() {
      const effectif = get(this.contract, 'risk.employeesBracket');
      if (effectif) {
        return `${effectif.min} à ${effectif.max}`;
      }
      return 'N/A';
    },
    getEffectiveDate() {
      if (this.contract.effectiveDate) {
        return this.toDateFrNumeric(this.contract.effectiveDate);
      }
      return 'N/A';
    },
    getEcheance() {
      return get(this.contract, 'echeance') || 'N/A';
    },
    getTerritorialite() {
      return get(this.contract, 'risk.equipment.territorialite') || 'N/A';
    },
    getEffectif() {
      const effectif = get(this.contract, 'risk.effectif');
      if (effectif) {
        if (effectif === 1) {
          return `${effectif} salarié`;
        }
        return `${effectif} salarié(s)`;
      }
      return 'N/A';
    },
  },
};
</script>
